/**
 * The invisible session behind the video application (START.md §4, §20).
 *
 * Nobody signs up, signs in, or picks a password. On first load the browser gets an
 * anonymous Firebase identity; that uid owns exactly one application document and is the
 * only thing the Pages Functions trust. App Check (reCAPTCHA v3) rides along so the API
 * is not an open door for scripts.
 *
 * Every import below is a deep subpath (`firebase/app`, `firebase/auth`,
 * `firebase/app-check`) so the bundle only carries those three products.
 */

import { getApps, initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import { getAuth, signInAnonymously, type Auth } from 'firebase/auth';
import {
  getToken as requestAppCheckToken,
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from 'firebase/app-check';

/**
 * Thrown at startup when the Firebase web config is missing. The app must fail loudly
 * here rather than half-work: without a session nothing can be saved.
 */
export class FirebaseConfigError extends Error {
  readonly missing: string[];

  /** Every `VITE_*` name this build DID receive — the fastest way to see what went wrong. */
  readonly present: string[];

  constructor(missing: string[], present: string[]) {
    super(FirebaseConfigError.describe(missing, present));
    this.name = 'FirebaseConfigError';
    this.missing = missing;
    this.present = present;
  }

  /**
   * Explains where the values are read from, because the usual cause is putting them somewhere
   * that looks right and is not.
   *
   * `VITE_*` values are compiled in **at build time** from `.env` at the repository root — the
   * `envDir` set in `apps/candidate/vite.config.ts`. Two near-misses account for almost every
   * occurrence of this error:
   *
   *   * putting them in `.dev.vars`, which is wrangler's file for the Pages Functions at
   *     runtime. Vite never reads it, so the browser bundle gets nothing;
   *   * editing `.env` without restarting the dev server. `import.meta.env` is baked in at build
   *     time, so a running server is still serving the old values.
   *
   * When `present` is empty, no `VITE_*` value arrived at all, which points at the file's
   * location rather than its contents.
   */
  private static describe(missing: string[], present: string[]): string {
    const lines = [
      `Firebase is not configured. Missing build-time values: ${missing.join(', ')}.`,
      '',
      present.length === 0
        ? 'This build received NO VITE_* values at all, so the problem is almost certainly WHERE'
        : `This build did receive: ${present.join(', ')}.`,
      present.length === 0 ? 'the values are, not what is in them.' : '',
      '',
      'VITE_* values are compiled in at build time, and are read ONLY from `.env` at the',
      'repository root (see `envDir` in apps/candidate/vite.config.ts). In particular:',
      '  • `.dev.vars` is wrangler\'s file for the API at runtime — Vite never reads it.',
      '  • Editing `.env` needs a dev-server restart; the old values stay compiled in until then.',
      '',
      'Run `npm run check:config` to see which file the values were found in.',
    ].filter((line) => line !== '');
    return lines.join('\n');
  }
}

/** Build-time values without which there is no point starting. */
const REQUIRED_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_APP_ID',
] as const;

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let appCheck: AppCheck | null = null;
let appCheckStarted = false;
let sessionPromise: Promise<string> | null = null;

function readOptions(): FirebaseOptions {
  const env = import.meta.env;
  const missing = REQUIRED_KEYS.filter((key) => !env[key]);
  if (missing.length > 0) {
    // Report what DID arrive, not just what did not. An empty list means the .env file was never
    // found; a partial list means it was found and is incomplete. Names only — a value could be
    // a token, and this string reaches the browser console.
    const present = Object.keys(env)
      .filter((key) => key.startsWith('VITE_') && env[key] !== undefined && env[key] !== '')
      .sort();
    throw new FirebaseConfigError([...missing], present);
  }
  return {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  };
}

/**
 * Is the build configured well enough to start a session? Used by the boot sequence to
 * show a plain explanation instead of a blank page.
 */
export function isFirebaseConfigured(): boolean {
  const env = import.meta.env;
  return REQUIRED_KEYS.every((key) => Boolean(env[key]));
}

/**
 * True when the site key could plausibly be a real reCAPTCHA key.
 *
 * A real one is around 40 characters. Handing reCAPTCHA a placeholder does not fail quietly — it
 * throws in the browser and fills the console — and App Check is optional here, so an obviously
 * fake value is treated the same as an absent one rather than as a configuration to attempt.
 */
function usableSiteKey(siteKey: string): boolean {
  return siteKey.length >= 30;
}

/**
 * App Check, best effort. The debug token has to be on `self` *before* `initializeAppCheck`
 * runs, which is why this lives inside the same lazy step as the app itself. With no site
 * key configured we simply run without App Check, so `npm run dev` works offline of reCAPTCHA.
 */
function startAppCheck(instance: FirebaseApp): void {
  if (appCheckStarted) return;
  appCheckStarted = true;

  const siteKey = (import.meta.env.VITE_APPCHECK_RECAPTCHA_SITE_KEY ?? '').trim();
  if (!usableSiteKey(siteKey)) {
    console.info(
      siteKey === ''
        ? '[altamont] App Check is not configured for this build; continuing without it.'
        : '[altamont] The App Check site key looks like a placeholder, so App Check is being ' +
            'skipped. Set a real VITE_APPCHECK_RECAPTCHA_SITE_KEY to enable it.',
    );
    return;
  }

  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN;
  if (debugToken) self.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;

  try {
    appCheck = initializeAppCheck(instance, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    console.error('[altamont] App Check could not start; continuing without it.', error);
    appCheck = null;
  }
}

/** Lazily create (once) the Firebase app and App Check. Throws {@link FirebaseConfigError}. */
function ensureApp(): FirebaseApp {
  if (!app) {
    app = getApps()[0] ?? initializeApp(readOptions());
    startAppCheck(app);
  }
  return app;
}

async function signIn(): Promise<string> {
  const instance = ensureApp();
  auth = getAuth(instance);
  await auth.authStateReady();
  if (auth.currentUser) return auth.currentUser.uid;
  const credential = await signInAnonymously(auth);
  return credential.user.uid;
}

/**
 * The uid of this browser's anonymous session, signing in on first call.
 *
 * Memoised, so the boot sequence, the router guard and the first API call share one
 * sign-in rather than racing three. A failure clears the memo so a later attempt can retry.
 */
export function ensureSession(): Promise<string> {
  if (!sessionPromise) {
    sessionPromise = signIn().catch((error: unknown) => {
      sessionPromise = null;
      throw error;
    });
  }
  return sessionPromise;
}

/**
 * A Firebase ID token for the `Authorization` header.
 *
 * Anonymous tokens expire after an hour and people take longer than that, so callers pass
 * `forceRefresh` after a 401 (see `lib/api.ts`).
 */
export async function getIdToken(forceRefresh = false): Promise<string> {
  await ensureSession();
  let user = auth?.currentUser ?? null;
  if (!user) {
    // The session was dropped underneath us (signed out, storage cleared). Start a new one.
    sessionPromise = null;
    await ensureSession();
    user = auth?.currentUser ?? null;
  }
  if (!user) throw new Error('Could not start a session for this browser.');
  return user.getIdToken(forceRefresh);
}

/**
 * An App Check token for the `X-Firebase-AppCheck` header, or `null` when App Check is not
 * configured or could not mint one. Never throws: a missing token is the server's decision
 * to make, and local development runs without reCAPTCHA at all.
 */
export async function getAppCheckToken(): Promise<string | null> {
  try {
    ensureApp();
    if (!appCheck) return null;
    const result = await requestAppCheckToken(appCheck);
    return result.token;
  } catch (error) {
    console.warn(
      '[altamont] Could not get an App Check token; sending the request without one.',
      error,
    );
    return null;
  }
}

/** The current uid, or `null` before {@link ensureSession} has resolved. */
export function currentUid(): string | null {
  return auth?.currentUser?.uid ?? null;
}
